<script lang="ts" setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { sendGoogleEvent } from '@/dashboard/helper';
import { DOWNLOAD_APP_LINK } from '@/dashboard/constants/links';
import Divider from '@/dashboard/vue/components/common/Divider.vue';
import { useThemeStore } from '@/dashboard/store/themeStore';
import { Draft, DraftType } from '@/dashboard/types/draft';
import { useDraftStore } from '@/dashboard/store/draftStore';
import { useDraftImageStore } from '@/dashboard/store/draftImageStore';
import AppleDownloadButton from '@/dashboard/vue/components/common/AppleDownloadButton.vue';

import appIcon from '../../../../../../assets/images/new_theme/icons/logo-ios.svg';

const props = withDefaults(
  defineProps<{
    draft: Draft | null;
  }>(),
  {
    draft: null,
  }
);

const themeStore = useThemeStore();
const draftStore = useDraftStore();
const draftImageStore = useDraftImageStore();

const { themeAuto, darkThemeEnabled } = storeToRefs(themeStore);
const { imageScaleDownMode, videoScaleDownMode, imageAutoscaling, followed, showDownloadBanner } = storeToRefs(draftStore);
const { originalImageSize, imageSize } = storeToRefs(draftImageStore);

const allowCommentModel = computed(() => {
  return props.draft ? props.draft.allow_comment : false;
});
const showAllowCommentSetting = computed(() => {
  if (props.draft?.draft_type !== DraftType.image) {
    return false;
  }

  return props.draft ? props.draft.owner_id === (window as any).g_opt.user.id : false;
});
const showScaleDownSetting = computed(() => {
  if (!props.draft) {
    return false;
  }

  return [DraftType.image, DraftType.video].includes(props.draft.draft_type);
});
const showFollow = computed(() => (window as any).g_opt.user.id && props.draft && props.draft.owner_id !== (window as any).g_opt.user.id);
const isSignedIn = computed(() => (window as any).g_opt.user.id !== 0);
const scaleDownMode = computed(() => {
  if (props.draft?.draft_type === DraftType.image) {
    return imageScaleDownMode.value;
  }
  if (props.draft?.draft_type === DraftType.video) {
    return videoScaleDownMode.value;
  }

  return false;
});

function changeTheme() {
  themeStore.themeChange();
}

function autoChange() {
  themeStore.autoThemeChange();
}

function changeAutoscaling() {
  if (originalImageSize.value.width && imageSize.value.width) {
    draftStore.changeAutoscaling(originalImageSize.value.width, imageSize.value.width);
  }
}

function changeScaleDownMode() {
  draftStore.changeScaleDownMode(props.draft?.draft_type);
}

async function changeAllowComment() {
  await draftStore.allowDraftCommenting();
}

async function follow() {
  await draftStore.followDraft();
}

async function closeDownloadBanner() {
  draftStore.closeDownloadBanner();
}

function download() {
  sendGoogleEvent('download_application');
}

function signIn() {
  sendGoogleEvent('sign_in_from_draft');
}
</script>

<template>
  <div class="shadow-menu-s absolute left-[26px] top-[44px] flex gap-[12px] rounded-16 bg-white p-[12px] dark:bg-gray-900">
    <div class="flex w-[286px] flex-col justify-between py-[12px]">
      <div class="flex-col">
        <div v-if="showAllowCommentSetting" class="group/item">
          <div class="mb-[23px] pl-[12px] pr-[12px]">
            <div class="mb-[9px] flex items-center">
              <label class="tppr-checkbox color-cta mr-[12px]" @click="changeAllowComment()">
                <span class="tppr-checkbox__input" :class="{ checked: allowCommentModel }" />
                <span class="tppr-checkbox__presenter" />
              </label>
              <span class="text-buttons text-black dark:text-white">Enable commenting</span>
            </div>

            <p class="text-caption-s text-gray-600 dark:text-white">Everyone will be able to comment on the screenshot.</p>
          </div>

          <Divider class="divider-gray-450 dark:divider-gray-700 mb-m group-last/item:hidden" />
        </div>

        <div class="group/item">
          <div class="mb-[22px] pl-[12px] pr-[12px]">
            <div class="mb-[8px] flex items-center">
              <label class="tppr-switch mr-[12px]" @click="changeTheme()">
                <span class="tppr-switch__input" :class="{ checked: !darkThemeEnabled }" />
                <span class="tppr-switch__presenter">
                  <span class="tppr-switch__presenter__circle">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="2.5" stroke-linejoin="round" />
                      <path d="M8 1V3" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M15 8L13 8" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M12.9497 12.9497L11.5355 11.5355" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M12.9497 3.05029L11.5355 4.46451" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M8 13L8 15" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M3 8L1 8" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M4.46436 4.46436L3.05014 3.05014" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M4.46436 11.5356L3.05014 12.9499" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </span>
                </span>
              </label>
              <span class="text-buttons text-black dark:text-white">Light: {{ darkThemeEnabled ? 'OFF' : 'ON' }}</span>
            </div>

            <p class="text-caption-s mb-[9px] text-gray-600 dark:text-white">The theme settings will be customized only for you.</p>

            <div class="flex items-center">
              <label class="tppr-checkbox color-cta mr-[12px]" @click="autoChange">
                <span class="tppr-checkbox__input" :class="{ checked: themeAuto }" />
                <span class="tppr-checkbox__presenter" />
              </label>
              <span class="text-buttons text-black dark:text-white">Auto-switching</span>
            </div>
          </div>

          <Divider class="divider-gray-450 dark:divider-gray-700 mb-m group-last/item:hidden" />
        </div>

        <div v-if="showScaleDownSetting" class="group/item">
          <div class="mb-[22px] pl-[12px] pr-[12px]">
            <div class="mb-[8px] flex items-center">
              <label class="tppr-switch mr-[12px]" @click="changeScaleDownMode()">
                <span class="tppr-switch__input" :class="{ checked: scaleDownMode }" />
                <span class="tppr-switch__presenter">
                  <span class="tppr-switch__presenter__circle"></span>
                </span>
              </label>
              <span class="text-buttons text-black dark:text-white">Scale down: {{ scaleDownMode ? 'ON' : 'OFF' }}</span>
            </div>

            <p class="text-caption-s mb-[9px] text-gray-600 dark:text-white">For proper display on screens with higher pixel density.</p>

            <div v-if="draft?.draft_type === DraftType.image" class="flex items-center">
              <label class="tppr-checkbox color-cta mr-[12px]" @click="changeAutoscaling()">
                <span class="tppr-checkbox__input" :class="{ checked: imageAutoscaling }" />
                <span class="tppr-checkbox__presenter" />
              </label>
              <span class="text-buttons text-black dark:text-white">Autoscaling</span>
            </div>
          </div>

          <Divider class="divider-gray-450 dark:divider-gray-700 mb-m group-last/item:hidden" />
        </div>

        <div v-if="showFollow" class="group/item">
          <div class="mb-[21px] pl-[12px] pr-[12px]">
            <div class="mb-[15px] flex w-[112px] items-center">
              <button class="tppr-button s" :class="{ 'white hover:!bg-gray-200': followed, colored: !followed }" @click="follow">
                <span class="tppr-button__text">{{ followed ? 'Unfollow' : 'Follow' }}</span>
              </button>
            </div>

            <p class="text-caption-s text-gray-600 dark:text-white">To receive notifications about new comments.</p>
          </div>

          <Divider class="divider-gray-450 dark:divider-gray-700 mb-m group-last/item:hidden" />
        </div>

        <div v-if="!isSignedIn" class="group/item">
          <div class="mb-[21px] flex justify-center">
            <div class="flex w-[138px] items-center">
              <a href="/sign_in" class="tppr-button m colored" @click="signIn">
                <span class="tppr-button__text">Sign In</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="flex-col">
        <Divider class="divider-gray-450 dark:divider-gray-700 mb-m" />

        <div class="flex justify-center">
          <a href="https://u24.gov.ua/" target="_blank">
            <svg xmlns="http://www.w3.org/2000/svg" width="131" height="13" viewBox="0 0 131 13" fill="none">
              <g clip-path="url(#clip0_3923_60647)">
                <path
                  class="fill-black dark:fill-white"
                  d="M38.6723 1.99744H45.2388V12.597H38.6723V1.99744ZM11.5695 6.47939C11.5695 7.99347 10.772 8.706 9.06793 8.706C7.36388 8.706 6.56546 7.99441 6.56546 6.47939V1.99744H0V6.73731C0 11.1738 2.37133 12.7482 9.06793 12.7482C15.7645 12.7482 18.1359 11.171 18.1359 6.73731V1.99744H11.5695V6.47939ZM45.9326 5.93482H51.4265V12.597H57.993V5.93482H63.4872V1.99744H45.9326V5.93482ZM31.3546 7.49437L26.2202 1.99744H18.9777V12.597H25.4563V7.10005L30.5863 12.597H37.8323V1.99744H31.3546V7.49437ZM114.355 4.23982C114.355 1.34802 111.911 0 106.762 0C101.209 0 98.8656 1.54471 98.8656 5.239H104.028C104.028 3.96705 104.882 3.51247 106.574 3.51247C108.222 3.51247 108.744 3.81582 108.744 4.28525C108.744 4.90594 108.556 4.90594 104.401 5.91997C99.8031 7.05552 98.3757 9.29698 98.3757 11.4623V12.5979H113.981V9.15687H104.853C105.404 8.61136 106.965 8.46014 110.422 7.76526C112.721 7.3125 114.355 6.47939 114.355 4.23982ZM64.1844 12.597H79.124V9.4352H70.4628V8.54178H76.6241V6.05543H70.4628V5.16201H79.1259V1.99744H64.1844V12.597ZM131 6.96462V10.5077H128.657V12.597H123.322V10.5077H114.514V6.93027L120.834 0.419344H128.658V6.96091L131 6.96462ZM123.321 3.48185L119.953 6.96462H123.321V3.48185ZM98.5908 7.30138C98.5908 10.8593 96.4501 12.6007 92.0387 12.6007H79.9634V1.99744H92.0387C96.4501 1.99744 98.5908 3.73883 98.5908 7.30138ZM92.184 7.30138C92.184 6.10457 91.547 5.54424 90.2168 5.54424H86.37V9.05762H90.2168C91.547 9.05392 92.184 8.49355 92.184 7.30138Z"
                />
              </g>
              <defs>
                <clipPath id="clip0_3923_60647">
                  <rect width="131" height="13" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </a>
        </div>
      </div>
    </div>

    <div v-if="showDownloadBanner" class="relative">
      <div class="relative flex h-[494px] w-[360px] items-center justify-center overflow-hidden rounded-[14px]">
        <span class="banner-download-app absolute"></span>

        <div
          class="relative flex h-[414px] w-[280px] flex-col items-center justify-end overflow-hidden rounded-16 border-[1px] border-solid border-[#FFFFFF52] bg-[#ECECEC80] pb-s shadow-[inset_0_0_12px_rgba(255,255,255,0.8)] backdrop-blur-[44px]"
        >
          <div class="mb-[43px] drop-shadow-[0px_-36px_76px_rgba(255,255,255,0.85)]">
            <img :src="appIcon" alt="" class="h-[128px] w-[128px]" />
          </div>

          <p class="text-caption-s mb-[27px] max-w-[216px] text-center text-black">Get the best screen capture tool ever and use it for free!</p>

          <a :href="DOWNLOAD_APP_LINK" target="_blank" @click="download">
            <apple-download-button class="tppr-button__animation hover:text-brand-cta active:text-brand-night-blue" />
          </a>
        </div>
      </div>

      <button class="absolute right-[17px] top-[17px] flex h-[24px] w-[24px] items-center justify-center stroke-white" @click="closeDownloadBanner()">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M1 1L15 15" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M1 15L15 0.999999" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>
  </div>
</template>
